// English UI strings — mirror of `es.ts`. Typed as `I18nDict`, so it MUST have
// exactly the same keys (the typecheck fails otherwise). Keep both in sync.

import type { I18nDict } from './es';

export const en: I18nDict = {
  app: {
    nombre: 'Control Panel',
    organizacion: 'Metamorfosis Origen',
    equipo: 'AstroYoga',
    descripcion: 'Integral management for Metamorfosis Origen: sales, content, meetings and leads.',
    cargando: 'Loading...',
  },
  tema: {
    cambiar: 'Change theme',
    claro: 'Light',
    oscuro: 'Dark',
    sistema: 'System',
  },
  idioma: {
    cambiar: 'Change language',
    espanol: 'Español',
    ingles: 'English',
  },
  nav: {
    inicio: 'Home',
    bandeja: 'Inbox',
    publicar: 'Publisher',
    tareas: 'Tasks',
    conexiones: 'Connections',
    sistema: 'System',
    usuarios: 'Users',
    campanas: 'Campaigns',
    anuncios: 'Ads',
    metricas: 'KPIs',
    miPerfil: 'My profile',
    menuUsuario: 'User menu',
  },
  roles: {
    ADMINISTRADOR: 'Administrator',
    GERENCIA: 'Management',
    LIDER: 'Lead',
    COLABORADOR: 'Contributor',
    VISUALIZADOR: 'Viewer',
  },
  auth: {
    login: {
      titulo: 'Sign in',
      tituloError: 'We could not sign you in',
      email: 'Email',
      password: 'Password',
      entrar: 'Sign in',
      entrando: 'Signing in...',
      olvideMiPassword: 'Forgot your password? Contact your administrator.',
      heroTitulo: 'Control Panel',
    },
    cambiarPassword: {
      titulo: 'Change password',
      descripcion: 'You need to change your password before continuing.',
      passwordActual: 'Current password',
      passwordNuevo: 'New password',
      passwordNuevoConfirmacion: 'Confirm new password',
      politicaAyuda: 'At least 10 characters, with uppercase, lowercase, number and symbol.',
      guardar: 'Save',
      guardando: 'Saving...',
      exito: 'Password updated. Redirecting...',
    },
    logout: 'Sign out',
  },
  admin: {
    usuarios: {
      titulo: 'Users',
      crear: 'Create user',
      cargando: 'Loading users...',
      vacio: 'No users yet.',
      sinResultados: 'No users match the filter.',
      buscarPlaceholder: 'Search by name or email…',
      filtroEmailPlaceholder: 'Filter email…',
      filtroNombrePlaceholder: 'Filter name…',
      limpiar: 'Clear',
      filtroRolTodos: 'All roles',
      filtroEstado: {
        todos: 'All',
        activos: 'Active',
        inactivos: 'Inactive',
      },
      totalSingular: 'user total',
      totalPlural: 'users total',
      col: {
        email: 'Email',
        nombre: 'Name',
        rol: 'Role',
        activo: 'Status',
        acciones: 'Actions',
        editar: 'Edit',
        resetPassword: 'Reset password',
      },
      estado: {
        activo: 'Active',
        inactivo: 'Inactive',
        debeCambiarPassword: 'Must change password',
      },
      crear_dialog: {
        titulo: 'Create user',
        descripcion: 'If you leave the password blank, we generate a temporary one you can copy at the end.',
        email: 'Email',
        nombre: 'Full name',
        rol: 'Role',
        rolPlaceholder: 'Choose role...',
        crear: 'Create',
        creando: 'Creating...',
        cancelar: 'Cancel',
        cerrar: 'Close',
        copiar: 'Copy',
        copiada: 'Password copied to clipboard',
        exito: 'User created',
        errorEmailEnUso: 'A user with that email already exists',
        passwordGeneradaTitulo: 'Temporary password generated',
        passwordGeneradaDescripcion:
          'This is the only time you will see it. Copy it and share it with the user through a secure channel.',
      },
      editar_dialog: {
        titulo: 'Edit user',
        nombre: 'Full name',
        rol: 'Role',
        activo: 'Status',
        guardar: 'Save changes',
        guardando: 'Saving...',
        cancelar: 'Cancel',
        exito: 'User updated',
      },
      reset_dialog: {
        titulo: 'Reset password',
        advertencia:
          'You will generate a new temporary password and the user will have to change it at next login. Failed attempts and lockout are reset.',
        resetear: 'Reset password',
        reseteando: 'Resetting...',
        cancelar: 'Cancel',
        cerrar: 'Close',
        copiar: 'Copy',
        copiada: 'Password copied to clipboard',
        exito: 'Password reset',
        generadaTitulo: 'Temporary password generated',
        generadaDescripcion:
          'This is the only time you will see it. Copy it and share it with the user through a secure channel.',
      },
    },
    campanas: {
      titulo: 'Ads',
      intro:
        'Where leads come from. Each ad tags the people it brings: by the ad text, by the Meta metadata or by the template the n8n Emisor used to send. Campaigns the app sends live in Campaigns.',
      crear: 'New ad',
      cargando: 'Loading campaigns...',
      vacio: 'No campaigns.',
      columnas: {
        nombre: 'Name',
        tipo: 'Type',
        numeros: 'Numbers',
        vigencia: 'Validity',
        textos: 'Texts',
        leads: 'Leads',
        activa: 'Active',
      },
      tipos: {
        META_ADS: 'Meta Ads',
        REAPERTURA: 'Reopening (n8n Emisor)',
        EMISION: 'Sent from the app',
      },
      sinHasta: 'no end',
      activar: 'Activate',
      desactivar: 'Deactivate',
      actualizada: 'Campaign updated',
      verTextos: '{n} text(s)',
      editar: 'Edit',
      dialogo: {
        titulo: 'New campaign',
        nombre: 'Name',
        tipo: 'Type',
        numeros: 'Numbers it runs on',
        textos: 'Texts that identify the lead',
        textosAyuda:
          'Separate texts with a blank line. For Meta Ads: the prefilled ad message. For reopening: the Emisor templates (you can use {{nombre}}).',
        vigenteDesde: 'Valid from',
        vigenteHasta: 'Valid until (optional)',
        cancelar: 'Cancel',
        crear: 'Create campaign',
        creando: 'Creating...',
        exito: 'Campaign created',
        errorNombreEnUso: 'A campaign with that name already exists.',
        errorNumeros: 'Choose at least one number.',
        errorNombre: 'The name must be at least 2 characters long.',
      },
      editarDialogo: {
        titulo: 'Edit campaign',
        textos: 'Texts that identify the lead',
        textosAyuda:
          'Separate texts with a blank line. Name, type and numbers can’t be changed — create a new campaign if you need that.',
        vigenteHasta: 'Valid until (optional)',
        cancelar: 'Cancel',
        guardar: 'Save changes',
        guardando: 'Saving...',
        exito: 'Campaign updated',
        errorTextos: 'Each text must be at least 3 characters long.',
      },
    },
    metricas: {
      titulo: 'KPIs',
      intro: 'Messages, response rate and block rate. Filter by date or view the full history.',
      campana: 'Campaign',
      todasLasCampanas: 'All campaigns',
      desde: 'From',
      hasta: 'To',
      limpiarFiltro: 'View all',
      cargando: 'Calculating...',
      recontacto: {
        titulo: 'AstroYoga follow-up campaign',
        intro:
          'The history of the AstroYoga campaign (the n8n sender over Notion Master Leads, non-scraped leads with a phone). Read from the snapshot saved when Notion was frozen: it no longer changes. New campaigns show their results in Campaigns.',
        aContactar: 'Leads to contact',
        contactados: 'Contacted',
        pendientes: 'Pending',
        convertidos: 'Converted',
        tasaBloqueo: 'They blocked us',
        tasaBloqueoAyuda: '{bloqueados} of {contactados} contacted by the sender were undelivered for 48 h',
        losBloqueamos: 'We blocked them',
        losBloqueamosAyuda:
          'removed from the app · plus {antes} marked "blocked me" before the campaign (not counted)',
      },
      app: {
        titulo: 'Meta Ads and the app',
        intro: 'What goes through this app: the ad leads and the inbox conversations.',
      },
      tarjetas: {
        mensajesEnviados: 'Messages sent',
        mensajesRecibidos: 'Messages received',
        tasaRespuesta: 'Response rate',
        tasaRespuestaAyuda: '{respondieron} of {contactados} contacted leads replied',
        yaContactados: 'Already contacted',
        pendientes: 'Pending contact',
        deTotal: 'of {total} leads',
        tasaBloqueo: 'Block rate',
        tasaBloqueoAyuda: '{bloqueados} of {total} leads in this period',
      },
    },
  },
  // Lead inbox (Cycle 1)
  bandeja: {
    titulo: 'Lead inbox',
    buscar: 'Search by name or phone',
    sinLeads: 'Nobody has written yet.',
    sinResultados: 'No lead matches the search.',
    elegiUnLead: 'Pick a conversation from the list.',
    cargando: 'Loading...',
    errorCarga: 'Could not load the inbox.',
    sinNombre: 'No name',
    sinTelefono: 'Number hidden by WhatsApp',
    noLeidos: '{n} unread',
    campana: 'Campaign',
    todasLasCampanas: 'All',
    todasLasEtapas: 'All',
    etiquetas: 'Tags',
    filtrosBtn: 'Filters',
    filtrosNinguno: 'none applied',
    orden: {
      titulo: 'Sort',
      fecha_desc: 'Most recent first',
      fecha_asc: 'Oldest first',
      alfabetico_asc: 'Alphabetical (A-Z)',
      alfabetico_desc: 'Alphabetical (Z-A)',
    },
    filtros: {
      todos: 'All',
      nuevos: 'New',
      activos: 'Active',
      esperando: 'Waiting',
      recontactados: 'Follow-ups',
      cerrados: 'Closed',
      historial: 'History',
      bloqueados: 'Deleted',
    },
    estados: {
      NUEVO: 'New',
      ACTIVO: 'Active',
      ESPERANDO_RESPUESTA: 'Waiting for reply',
      RECONTACTO_1: 'Follow-up 1',
      RECONTACTO_2: 'Follow-up 2',
      CERRADO_GANADO: 'Won',
      CERRADO_NO_INTERESADO: 'Not interested',
      CERRADO_SIN_RESPUESTA: 'No reply',
      DERIVADO_SALUD: 'Referred (health)',
      HISTORICO: 'History',
      BLOQUEADO: 'Deleted',
    },
    fuentes: {
      META_ADS: 'Meta Ads',
      ORGANICO: 'Organic',
      RECONTACTO_CAMPANA: 'Reactivation campaign',
    },
    deteccion: {
      AD_METADATA: 'from the ad',
      TEXTO: 'from the text',
      VIGENCIA: 'campaign in force',
      PLANTILLA: 'by the Emisor template',
      FALLBACK: 'new number during the campaign',
      NINGUNA: '',
    },
    etapa: 'Stage',
    etapas: {
      1: 'Contact',
      2: 'Deepen',
      3: 'Validate',
      4: 'Offer',
      5: 'Close',
    },
    tipoRecibido: {
      AUDIO: 'Audio not available',
      IMAGEN: 'Image received — see it on the phone',
      VIDEO: 'Video received — watch it on the phone',
      DOCUMENTO: 'Document received',
      STICKER: 'Sticker',
      OTRO: 'Unsupported message',
    },
    editado: 'edited',
    textoAntesDeEditar: 'It used to say:',
    eliminado: 'Message deleted in WhatsApp',
    editadoSinTexto: 'Edited in WhatsApp: the new text does not reach the app, check the phone',
    enviadoPor: {
      OPERADORA: 'from the app',
      TELEFONO: 'from the phone',
      RECONTACTO_AUTO: 'automatic follow-up',
      SISTEMA_APERTURA: 'automatic opening',
      CAMPANA: 'campaign',
      LEAD: '',
    },
    compositor: {
      placeholder: 'Type the reply… (Enter sends, Shift+Enter adds a line)',
      enviar: 'Send',
      enviando: 'Sending...',
      enviado: 'Message sent',
      errorEnvio: 'Could not send. WhatsApp did not answer; try again.',
      cerrado: 'This lead is closed. Writing reopens it.',
      bloqueado: 'This contact was deleted. It can’t be written to again.',
    },
    sugerencia: {
      aviso: 'The system suggested this message — review it before sending.',
      usar: 'Use this text',
      calificarEstrella: 'Rate this suggestion {n} of 5 stars',
      generarAhora: 'Generate suggestion now',
      generando: 'Generating…',
      esperando: 'Waiting for the next message before suggesting…',
      sinSugerencia: 'No suggestion yet for this lead.',
    },
    audio: {
      grabar: 'Record audio',
      grabando: 'Recording',
      detener: 'Stop recording',
      cancelar: 'Cancel recording',
      descartar: 'Discard',
      enviar: 'Send audio',
      enviando: 'Sending audio...',
      permisoDenegado: 'Could not access the microphone. Check your browser permissions.',
      errorEnvio: 'Could not send the audio. Try again.',
    },
    acciones: {
      cerrar: 'Close lead',
      cerrarGanado: 'Close as won',
      cerrarNoInteresado: 'Close: not interested',
      cerrado: 'Lead closed',
      errorCerrar: 'Could not close the lead.',
      cambiarEtapa: 'Change stage',
      sincronizar: 'Sync with WhatsApp',
      sincronizando: 'Syncing...',
      sincronizado: 'Synced: {procesados} new, {duplicados} already there, {ignorados} ignored.',
      indicadores: 'Indicators',
      eliminar: 'Delete contact',
      eliminado: 'Contact deleted',
      errorEliminar: 'Could not delete the contact.',
    },
    eliminarDialogo: {
      titulo: 'Delete contact',
      descripcion:
        'It gets hidden from the inbox and is never written to again from here. The record stays saved.',
      motivo: 'Reason (required)',
      motivoPlaceholder: 'E.g.: inappropriate content, asked not to be contacted again...',
      errorMotivo: 'Briefly state the reason (at least 3 characters).',
      cancelar: 'Cancel',
      confirmar: 'Delete',
      eliminando: 'Deleting...',
    },
    calidad: {
      abrir: 'Rate lead',
      explicacion: 'Rate this lead to improve the leads Meta brings.',
      opciones: { SIN_CALIFICAR: 'Not rated', BUENO: 'Good', BASURA: 'Junk', COMPRO: 'Bought' },
      porQue: 'Why?',
      motivos: {
        DESINTERESADO: 'Not interested',
        BUSCA_OTRA_COSA: 'Looking for something else',
        FUERA_DE_ZONA: 'Out of area',
        SPAM: 'Spam',
        INAPROPIADO: 'Inappropriate',
      },
      deshacer: 'Remove rating',
      error: 'Could not save the rating.',
    },
    tiempo: {
      ahora: 'now',
      minutos: '{n} min ago',
      horas: '{n} h ago',
      ayer: 'yesterday',
      dias: '{n} days ago',
    },
    numeros: {
      enviarDesde: 'Send from',
      preferido: 'Default number for this person',
      fijarPreferido: 'Use this number from now on',
      chatPor: 'Chat via {etiqueta}',
      vista: 'View',
      ambos: 'Both',
      sinMensajes: 'No messages via this number yet.',
      cambioAviso: 'You are about to write from a different number than the one they use. Only on purpose.',
    },
    conexion: {
      enVivo: 'Live',
      reconectando: 'Reconnecting...',
    },
  },
  errores: {
    auth: {
      credenciales: 'Invalid credentials',
      bloqueado: 'Your account is temporarily locked. Try again in a few minutes.',
      tasaLimite: 'You are doing this too often — wait a few seconds before retrying.',
      sinPermiso: 'You do not have permission for this action',
      sinPermisoDetalle: 'If you think this is a mistake, talk to your administrator.',
    },
    locking: {
      conflicto: 'Another user edited this while you were working. Do you want to see the changes?',
    },
    generico: {
      algoSalioMal: 'Something went wrong — it has been logged, try again.',
    },
  },
  erroresAdmin: {
    titulo: 'System errors',
    descripcion:
      'Unexpected errors (500) captured automatically. Mark as resolved the ones that already have a fix.',
    cargando: 'Loading errors...',
    vacio: 'No errors logged.',
    filtros: {
      todos: 'All',
      noResueltos: 'Unresolved',
      resueltos: 'Resolved',
    },
    col: {
      fecha: 'Date',
      nivel: 'Level',
      mensaje: 'Message',
      path: 'Endpoint',
      estado: 'Status',
      acciones: '',
    },
    estado: {
      resuelto: 'Resolved',
      pendiente: 'Pending',
    },
    detalle: 'View detail',
    marcarResuelto: 'Mark resolved',
    marcarPendiente: 'Mark pending',
    notas: 'Notes (optional)',
    placeholderNotas: 'e.g.: "Fix in PR-123 — date parser bug"',
    guardar: 'Save',
    cancelar: 'Cancel',
    exitoResolver: 'Status updated',
    stack: 'Stack trace',
    request: 'Request',
    contexto: 'Context',
  },
  configuracionAdmin: {
    titulo: 'System settings',
    descripcion: 'System runtime variables (key-value). Changes are audited.',
    cargando: 'Loading settings...',
    vacio: 'No settings recorded.',
    col: {
      clave: 'Key',
      valor: 'Value (JSON)',
      descripcion: 'Description',
      actualizadoEn: 'Last updated',
    },
    editar: 'Edit',
    guardar: 'Save',
    cancelar: 'Cancel',
    exito: 'Settings updated',
    invalidJson: 'Invalid JSON — check the format.',
    integracion: {
      titulo: 'Integration with other apps',
      apiKeys: 'API keys',
      apiKeysDesc: 'Create and revoke credentials for other apps to consume the API.',
      docs: 'API documentation (OpenAPI)',
    },
  },
  apiKeys: {
    titulo: 'API keys',
    descripcion:
      'Credentials to integrate other applications. Each key acts with an assigned role ' +
      '(the same permissions as that role). It is sent in the X-API-Key header.',
    docs: 'View API documentation',
    crear: 'Create API key',
    cargando: 'Loading API keys...',
    vacio: 'No API keys yet.',
    col: {
      nombre: 'Name',
      prefijo: 'Prefix',
      rol: 'Role',
      estado: 'Status',
      ultimoUso: 'Last used',
      creadoPor: 'Created by',
    },
    activa: 'Active',
    revocada: 'Revoked',
    nunca: 'Never',
    revocar: 'Revoke',
    confirmarRevocar: 'Revoke this API key? Apps using it will stop working.',
    revocada_exito: 'API key revoked',
    dialog: {
      titulo: 'Create API key',
      nombre: 'Name',
      nombrePlaceholder: 'e.g.: Power BI integration',
      rol: 'Role (key permissions)',
      crear: 'Create',
      cancelar: 'Cancel',
    },
    creada: {
      titulo: 'Copy the API key now',
      aviso: 'This is the only time the full key is shown. Store it somewhere safe.',
      copiar: 'Copy',
      copiado: 'Copied',
      cerrar: 'Done',
    },
  },
  notificaciones: {
    titulo: 'Notifications',
    abrirPanel: 'Open notifications',
    contadorTooltip: 'unread notifications',
    vacio: 'You have no notifications.',
    marcarTodasLeidas: 'Mark all as read',
    marcarLeida: 'Mark as read',
    irAlContexto: 'Go to context',
    soloNoLeidas: 'Unread only',
    todas: 'All',
    tipos: {
      SISTEMA: 'System',
      PUBLICACION: 'Publication',
      CONEXION: 'Connection',
      CAMPANA: 'Campaign',
    },
  },
  conexiones: {
    probar: 'Test connections',
    probando: 'Testing...',
    exito: 'Done.',
    columnas: {
      conexion: 'Connection',
      estado: 'Status',
      ultimoUso: 'Last successful use',
      ultimaPrueba: 'Last test',
      detalle: 'Detail',
    },
    estados: {
      OK: 'OK',
      ERROR: 'Error',
      SIN_PROBAR: 'Not tested',
    },
    nombres: {
      GOOGLE_DRIVE: 'Google Drive',
      YOUTUBE: 'YouTube',
      GEMINI: 'Gemini',
      META_ADS: 'Meta Ads',
      EVOLUTION: 'WhatsApp (Evolution)',
      BUFFER: 'Buffer (TikTok/Instagram/Facebook)',
    },
    nunca: 'never',
  },
  perfil: {
    titulo: 'My profile',
    cargando: 'Loading profile...',
    datosPersonales: {
      titulo: 'Personal information',
      descripcion: 'Edit your name and photo. Email and role are managed by an administrator.',
      nombre: 'Name',
      email: 'Email',
      rol: 'Role',
      guardar: 'Save changes',
      guardando: 'Saving...',
      exito: 'Profile updated.',
    },
    avatar: {
      titulo: 'Profile photo',
      subir: 'Upload photo',
      cambiar: 'Change photo',
      quitar: 'Remove photo',
      subiendo: 'Uploading...',
      quitando: 'Removing...',
      ayuda: 'JPEG, PNG or WebP. Max 2 MB.',
      exitoSubida: 'Photo updated.',
      exitoQuitada: 'Photo removed.',
      errorFormato: 'Only JPEG, PNG or WebP images are allowed.',
      errorTamanio: 'The image exceeds the 2 MB maximum.',
      alt: 'Profile photo',
    },
    password: {
      titulo: 'Password',
      descripcion: 'For better security, change your password periodically.',
      cambiar: 'Change password',
    },
  },
  // ciclo-panel
  inicio: {
    saludo: 'Hi, {nombre}',
    intro: 'Everything you use, in one place.',
    accesos: {
      ventas: { titulo: 'Sales', detalle: 'The WhatsApp inbox: leads, replies and follow-ups' },
      publicar: {
        titulo: 'Publisher',
        detalle: 'Upload a reel, a carousel or a story and it gets published',
      },
      reuniones: { titulo: 'Meetings', detalle: 'Classes and talks over video, on Meet' },
      tareas: { titulo: 'Tasks', detalle: 'Your priorities for the week, card by card' },
      campanas: { titulo: 'Campaigns', detalle: 'WhatsApp messages to a list, with their results in sight' },
    },
    abrirEnPestana: 'Opens in a new tab',
  },
  campanasEnvio: {
    titulo: 'Campaigns',
    intro:
      'WhatsApp messages the app sends to a fixed list, at its own pace. You decide whether they keep going, based on the results.',
    nueva: 'New campaign',
    cargando: 'Loading campaigns...',
    vacio: 'No campaigns yet. Create the first one with "New campaign".',
    estados: { BORRADOR: 'Draft', ACTIVA: 'Active', PAUSADA: 'Paused', TERMINADA: 'Finished' },
    activa: 'Active',
    progreso: '{enviados} of {total} sent',
    sinLista: 'The list is fixed when activated',
    verDetalle: 'See results',
    editar: 'Edit',
    activada: 'Campaign activated: within a minute it fixes the list and starts sending',
    pausada: 'Campaign paused',
    confirmarActivar: {
      titulo: 'Activate "{nombre}"?',
      cuerpo:
        'The list is fixed right now: the people who match the filters at this moment. Pace and messages can change later, but not who receives it.',
      confirmar: 'Activate',
      cancelar: 'Cancel',
    },
    resultados: {
      enviados: 'Sent',
      entregados: 'Delivered',
      respondieron: 'Replied',
      avanzaron: 'Moved forward',
      compraron: 'Bought',
      noEntregados: 'Blocked us',
      noEntregadosAyuda:
        'Not delivered within 48 h. WhatsApp does not report blocks: this is the only signal (it can also be a phone that is off).',
      sinWhatsApp: 'No WhatsApp',
      salteados: 'Skipped',
      pendientes: 'To send',
    },
    editor: {
      tituloNueva: 'New campaign',
      tituloEditar: 'Edit campaign',
      nombre: 'Name',
      nombrePlaceholder: 'E.g.: November workshop',
      pasoQuien: '1. Who',
      pasoMensajes: '2. Messages',
      pasoRitmo: '3. Pace',
      pasoProbar: '4. Test',
      listaFija: 'The list was fixed when activated: who receives it can no longer change.',
      guardar: 'Save',
      guardado: 'Campaign saved',
      volver: 'Back',
    },
    filtros: {
      quienes: 'Who',
      origenes: {
        APP: 'Wrote to us (app)',
        NOTION: 'Notion history',
        SCRAPER: 'Scraped businesses',
      },
      avisoScraper:
        'These are businesses taken from Google Maps, not people who wrote to us. Cold messages are what brings the most blocks, and about 90 of those phones have the wrong country prefix.',
      ultimoEntrante: 'Their last message',
      rangos: {
        MENOS_30: 'Less than 30 days',
        DE_30_A_90: '30 to 90 days',
        MAS_90: 'More than 90 days',
        NUNCA: 'Never wrote to us',
      },
      etapa: 'Stage',
      desde: 'from',
      hasta: 'to',
      calidad: 'Quality',
      calidades: { SIN_CALIFICAR: 'Not rated', BUENO: 'Good', BASURA: 'Junk', COMPRO: 'Bought' },
      fuente: 'Came from',
      fuentes: { META_ADS: 'Meta ad', ORGANICO: 'Organic', RECONTACTO_CAMPANA: 'Follow-up' },
      campanaOrigen: 'Source campaign',
      ninguna: 'None',
      pais: 'Country',
      descanso: 'Rest',
      descansoAyuda: 'Has not received any campaign in the last',
      dias: 'days',
      cualquiera: 'With none checked, it does not filter.',
      siempre:
        'Always left out: blocked, those who bought, junk, referred to health, those who said no, open chats with Ailén and anyone we wrote to less than 3 days ago.',
    },
    audiencia: {
      llegaria: 'It would reach {n} people',
      calculando: 'Calculating...',
      excluidos: 'Match the filters but are left out:',
      verMuestra: 'See 10 examples',
      ocultarMuestra: 'Hide',
      sinNombre: '(no name)',
    },
    plantillas: {
      ayuda:
        'Each send picks one at random among the active ones. {{nombre}} becomes the first name; if the person has no name, the "Hola {{nombre}}," is removed.',
      agregar: 'Add template',
      nombre: 'Name',
      texto: 'Text',
      activa: 'Active',
      vistaPrevia: 'This is what Ana gets:',
      vistaPreviaSinNombre: 'And someone without a name:',
      nueva: 'Template {n}',
    },
    ritmo: {
      topeDiario: 'Messages per day',
      intervalo: 'Minutes between messages',
      franja: 'Hours (Argentina)',
      de: 'from',
      a: 'to',
      horas: 'h',
      hasta: 'Ends on',
      ayudaTope: 'Also, each WhatsApp number sends at most 10 per day, across all campaigns.',
    },
    prueba: {
      ayuda:
        'Sends you the text right now, only to you. It does not count for the campaign. The number must have written to Metamorfosis at some point.',
      numero: 'Your number',
      plantilla: 'Template',
      enviar: 'Send me a test',
      enviada: 'Test sent through {instancia}',
    },
    detalle: {
      volver: 'Campaigns',
      envios: 'Who it was sent to',
      columnas: {
        persona: 'Person',
        estado: 'Status',
        numero: 'Number',
        enviado: 'Sent',
        respondio: 'Replied',
      },
      estadosEnvio: {
        PENDIENTE: 'To send',
        ENVIADO: 'Sent',
        ENTREGADO: 'Delivered',
        NO_ENTREGADO_48H: 'Not delivered (48 h)',
        SIN_WHATSAPP: 'No WhatsApp',
        SALTEADO: 'Skipped',
      },
      verCharla: 'Open chat',
      si: 'Yes',
      ritmoActual: '{tope} per day · every {intervalo} min · from {inicio} to {fin} h',
      sinEnvios: 'Nobody in this view yet.',
      mostrar: 'Show',
      todos: 'All',
    },
  },
  embudo: {
    volver: 'Back to inbox',
    campana: 'Campaign',
    filtrarPorFecha: 'Filter by date',
    desde: 'From',
    hasta: 'To',
    limpiarFiltro: 'View all',
    cargando: 'Loading...',
    costoPorMensaje: 'Cost per message',
    costoPorCliente: 'Cost per closed client',
    sinDatos: 'No data yet',
    etapas: {
      '1': 'Contact',
      '2': 'Deepen',
      '3': 'Validate',
      '4': 'Proposal',
      5: 'Close',
    },
    gastoPorDia: 'Campaign spend per day',
    gastoTotal: 'Total: {monto}',
    etapaPrefijo: 'STAGE',
    leads: 'leads',
    avanza: 'advance',
    enRango: 'on track',
    revisar: 'review',
    vigilar: 'watch',
    muestraChica: 'small sample',
    sinCierres: 'No one has reached this stage yet in this range.',
    ganados: 'Won',
    perdidos: 'Lost',
    enProceso: 'In progress',
    calidad: {
      porAnuncio: 'Quality by ad',
      porCampana: 'Quality by campaign',
      anuncio: 'Ad',
      campana: 'Campaign',
      leads: 'Leads',
      buenos: 'Good',
      basura: 'Junk',
      sinCalificar: 'Not rated',
      sinRespuesta: 'No reply',
      gasto: 'Spend',
      costoPorLeadBueno: 'Cost per good lead',
      sinAnuncio: 'No ad identified',
      sinNombre: 'Ad {id}',
      total: 'Total',
      vacio: 'No Meta leads or spend in this range yet.',
      muestra: 'of {n} rated',
    },
  },
  publicar: {
    titulo: 'Content publisher',
    intro: 'Pick the file, write the text and say when. Uploading and publishing is on the system.',
    formato: { etiqueta: 'What are you publishing', REEL: 'Reel', CARRUSEL: 'Carousel', HISTORIA: 'Story' },
    archivo: {
      etiqueta: 'The file',
      etiquetaCarrusel: 'The images, in order',
      arrastrar: 'Drop here or click to choose',
      ayudaReel: 'One video (mp4 or mov), up to 95 MB',
      ayudaHistoria: 'One image or video, up to 95 MB',
      ayudaCarrusel: 'Several images (jpg or png). Published in this order',
      quitar: 'Remove',
      subir: 'Move up',
      bajar: 'Move down',
      muyGrande: 'That file is over 95 MB',
      tipoInvalido: 'That file type does not match this format',
    },
    tituloCampo: 'Title',
    tituloAyuda: 'Internal: it names the file and sorts the list',
    caption: 'Post text',
    captionContador: '{n} of 2,200',
    hashtags: 'Hashtags',
    hashtagsAyuda: 'Space separated. Appended to the text',
    redes: {
      etiqueta: 'Where',
      INSTAGRAM: 'Instagram',
      TIKTOK: 'TikTok',
      YOUTUBE: 'YouTube',
      FACEBOOK: 'Facebook',
      ayudaYoutube: 'YouTube only takes video',
    },
    pilar: {
      etiqueta: 'Pillar',
      ninguno: 'No pillar',
      ALCANCE: 'Reach',
      POSICIONAMIENTO: 'Positioning',
      CONVERSION: 'Conversion',
    },
    modo: { etiqueta: 'When', PROGRAMAR: 'Schedule', PUBLICAR_YA: 'Publish now' },
    fecha: 'Date',
    hora: 'Time',
    notas: 'Internal notes',
    notasAyuda: 'Not published',
    boton: 'Publish',
    subiendo: 'Uploading… {pct}%',
    faltan: 'Before publishing:',
    listo: {
      ya: 'Done: it publishes in the next hours on {redes}.',
      programado: 'Done: it publishes on {fecha} at {hora} on {redes}.',
      horizonte: 'Done: scheduled for {fecha} at {hora}. The system picks it up 5 days before.',
      otra: 'Add another',
    },
    pestanas: { cargar: 'Upload', log: 'Upload log', calendario: 'Calendar', indicadores: 'Insights' },
    indicadores: {
      rango: {
        semana: 'Last week',
        mes: 'Last month',
        tresMeses: 'Last 3 months',
        personalizado: 'Custom',
      },
      alcance: 'Reach',
      visualizaciones: 'Views',
      interacciones: 'Interactions',
      tiempoReproduccion: 'Watch time',
      publicaciones: '{n} posts',
      sinPublicaciones: 'No posts this week',
      cargando: 'Loading...',
      sinDatos: 'No content data yet — the first sync can take up to 24 h.',
      sinRedes: 'Pick at least one network to see the insights.',
      segundos: 's',
    },
    calendario: {
      mesAnterior: 'Previous month',
      mesSiguiente: 'Next month',
      hoy: 'Today',
      vistas: { dia: 'Day', semana: 'Week', mes: 'Month' },
      semanaDel: 'Week of {desde} to {hasta}',
      sinDia: 'No posts that day.',
      dias: 'Mon|Tue|Wed|Thu|Fri|Sat|Sun',
      meses: 'January|February|March|April|May|June|July|August|September|October|November|December',
      vacio: 'No posts this month.',
      mas: '+{n} more',
    },
    lista: {
      titulo: 'Already loaded',
      cargando: 'Loading…',
      vacio: 'No posts yet.',
      sinFecha: 'no date',
      estados: {
        Borrador: 'Draft',
        Pendiente: 'Pending',
        Programado: 'Scheduled',
        Publicado: 'Published',
        Error: 'Error',
      },
    },
    errores: {
      driveNoConfigurado: 'Google Drive is not connected yet. Tell Javier.',
      drive: 'Could not upload the file to Drive. Try again.',
    },
  },
  // ciclo-tareas
  tareas: {
    crear: 'Create task',
    vacio: 'You don’t have any tasks yet. Create the first one with the button above.',
    prioridad: {
      etiqueta: 'Priority',
      ALTA: 'High',
      MEDIA: 'Medium',
      BAJA: 'Low',
    },
    activar: 'Activate',
    desactivar: 'Deactivate',
    desactivada: 'Not this week',
    completar: 'Mark as done',
    descompletar: 'Mark as pending',
    pestanas: { tareas: 'Tasks', estadisticas: 'Statistics' },
    dias: 'Mon|Tue|Wed|Thu|Fri|Sat|Sun',
    sinFecha: 'No date',
    vistas: { etiqueta: 'View', lista: 'List', semana: 'Week' },
    vistaSemana: {
      hoy: 'Today',
      vacio: 'Nothing for this day',
      arrastrar: 'Drag to another day',
    },
    objetivo: {
      etiqueta: 'Goal for the week',
      placeholder: 'Goal for the week: what do you want to achieve?',
      guardar: 'Save goal',
      guardando: 'Saving...',
      cancelar: 'Cancel',
      editar: 'Change the goal',
      errorVacio: 'Write the goal for the week',
      exito: 'Goal saved',
      smart: {
        boton: 'How to write a good goal (SMART)',
        titulo: 'SMART: a well-defined goal is',
        S: { nombre: 'Specific', detalle: 'It says exactly what you will achieve, nothing vague.' },
        M: { nombre: 'Measurable', detalle: 'It has a number or a clear sign to know whether it was met.' },
        A: { nombre: 'Achievable', detalle: 'It is possible with this week’s time and resources.' },
        R: { nombre: 'Relevant', detalle: 'It truly matters: it moves the business where it wants to go.' },
        T: { nombre: 'Time-bound', detalle: 'It has a deadline: this week, or a specific day.' },
        ejemploTitulo: 'Example',
        ejemploMal: 'Not: "Improve sales".',
        ejemploBien: 'Yes: "Reply to every new lead within 24 hours, Monday to Friday".',
      },
    },
    semana: {
      planificando: 'Week {n} · {mes} · planning',
      enCurso: 'Week {n} · {mes} · in progress',
      guardar: 'Save planning',
      guardando: 'Saving...',
      cerrar: 'Close week',
      cerrando: 'Closing...',
      confirmarCerrar: 'Close Week {n}? The result is saved to history, checks reset, and next week begins.',
      exitoGuardada: 'Planning saved — Week {n} in progress',
      exitoCerrada: 'Week {n} closed — Week {siguiente} began',
      errorNoPlanificada: 'The week is already saved — the task list can’t be changed until it’s closed.',
      errorNoGuardada: 'You’re still planning — save the week before checking off tasks.',
    },
    dialogo: {
      tituloCrear: 'Create task',
      tituloEditar: 'Edit task',
      titulo: 'Title',
      descripcion: 'Description',
      descripcionOpcional: 'Description (optional)',
      guardar: 'Save',
      guardando: 'Saving...',
      crear: 'Create',
      creando: 'Creating...',
      cancelar: 'Cancel',
      eliminar: 'Delete task',
      eliminando: 'Deleting...',
      confirmarEliminar: 'Delete this task? Weeks already recorded stay in the history.',
      errorTitulo: 'The title cannot be empty',
      vence: 'Due on',
      errorVence: 'Pick a day of this week ({inicio} to {fin})',
      exitoCreada: 'Task created',
      exitoEditada: 'Task updated',
      exitoEliminada: 'Task deleted',
    },
    estadisticas: {
      vacio: 'No closed weeks yet — close the current week to see the first data point.',
      totalTareas: 'Tasks with history',
      totalSemanas: 'Closed weeks',
      cumplimientoGlobal: 'Overall completion',
      graficoTitulo: 'Completion week by week',
      cumplimiento: '{pct}% completion',
      semanasRegistradas: '{n} week(s) recorded',
      peorTarea: 'The one you struggle with most: {titulo}',
      objetivosTitulo: 'Goal of each week',
      sinObjetivo: 'No goal',
    },
  },
  sistema: {
    titulo: 'System',
    intro: 'VPS status as left by the collector. Read only.',
    tomado: 'Taken {min} min ago',
    viejo: 'This data is over 2 hours old: the collector may be down.',
    sinDatos: 'No data from the collector',
    pestanas: { lineas: 'By line', tipos: 'By type', exposicion: 'Exposure', avisos: 'Alerts' },
    kpis: {
      contenedores: 'containers up',
      workflows: 'active workflows',
      fuera: 'off expected',
      publicos: 'ports visible outside',
      unaCapa: 'behind a single layer',
      disco: 'disk',
      ram: 'free RAM · load',
      uptime: 'uptime',
      leads: 'scraped leads',
      backup: 'last backup',
    },
    lineas: {
      ayuda: 'Each card is a line of work. Click to see its pieces.',
      esperado: 'expected',
      cerrar: 'Close',
      recibeDe: 'receives from',
      mandaA: 'sends to',
      externos: 'external',
    },
    tipos: {
      contenedor: 'Containers',
      workflow: 'n8n workflows',
      servicio: 'Host services',
      puerto: 'Local processes',
      instancia_wa: 'WhatsApp numbers',
      activosDe: '{n} active of {total}',
    },
    exposicion: {
      intro:
        'What the host listens on, what separates it from the internet and what is visible outside. Docker publishes via FORWARD and bypasses iptables INPUT DROP.',
      capas: 'The 12 layers',
      bandas: {
        expuesto: 'Answers from the internet and was NOT expected',
        una_capa: 'Behind ONE single layer',
        ok: 'Expected, two layers, or local only',
      },
      ninguno: 'None.',
      columnas: {
        puerto: 'port',
        servicio: 'service',
        escucha: 'listens on',
        separa: 'separates it from the internet',
        sondeo: 'probe',
      },
      todas: '0.0.0.0 (all)',
      local: '127.0.0.1',
      privada: 'private network',
      abierto: 'OPEN',
      previsto: 'expected',
    },
    avisos: { vacio: 'Nothing off expected.' },
  },
};
